import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../services/api';
import AuthLayout from '../components/Layout/AuthLayout';

/**
 * TeacherRegisterPage - Two-step registration form for teachers
 * Step 1: Validate activation code and load pre-filled data
 * Step 2: Complete registration with email and password
 */
const TeacherRegisterPage = () => {
  const [step, setStep] = useState(1); // 1 = Enter Code, 2 = Complete Registration
  const [activationCode, setActivationCode] = useState('');
  const [teacherData, setTeacherData] = useState(null);
  const [schoolData, setSchoolData] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { registerTeacher } = useAuth();

  // Step 1: Validate activation code
  const handleValidateCode = async (e) => {
    e.preventDefault();
    setError('');

    if (!activationCode.trim()) {
      setError('Please enter your activation code');
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiRequest('/api/auth/teacher/validate-code', 'POST', {
        activation_code: activationCode
      });

      setTeacherData(response.teacher_data);
      setSchoolData(response.school_data);
      setStep(2);
    } catch (err) {
      setError(err.message || 'Invalid activation code. Please check and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Complete registration
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      await registerTeacher(
        formData.email,
        formData.password,
        teacherData.full_name,
        teacherData.subject,
        activationCode
      );

      setSuccess('Account created successfully! Redirecting to login...');

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login/teacher');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <AuthLayout showBackButton={step === 1}>
      <form className="w-full" onSubmit={step === 1 ? handleValidateCode : handleSubmit}>
        {step === 2 && (
          <button
            type="button"
            onClick={() => {
              setStep(1);
              setTeacherData(null);
              setSchoolData(null);
              setError('');
            }}
            className="mb-6 text-cyan-600 hover:text-cyan-700 text-sm flex items-center font-semibold"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Change activation code
          </button>
        )}

        {/* Icon & Title */}
        <div className="text-center mb-8">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 blur-3xl opacity-40 bg-cyan-500" />
            <div className="relative bg-gradient-to-br from-cyan-500 to-blue-600 rounded-[1.5rem] p-6 shadow-2xl">
              <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-6M18 7v6" />
              </svg>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">
            Teacher Registration
          </h2>
          <p className="text-gray-500 text-base">
            {step === 1 ? 'Enter your activation code to begin' : `Join ${schoolData?.school_name || 'your school'} on Brillabee`}
          </p>
        </div>

        {/* Step 1: Enter and Validate Activation Code */}
        {step === 1 && (
          <>
            <div className="form-group mb-6 p-5 bg-cyan-50 rounded-2xl border-2 border-cyan-200">
              <label htmlFor="teacher-code" className="block mb-3 font-bold text-cyan-900 text-sm">
                Teacher Activation Code*
              </label>
              <input
                type="text"
                id="teacher-code"
                name="teacherCode"
                placeholder="TEACH-XXXXXXXX"
                className="w-full py-3 px-4 border-2 border-cyan-300 rounded-xl bg-white focus:outline-none focus:border-cyan-500 font-mono text-lg transition-colors"
                value={activationCode}
                onChange={(e) => setActivationCode(e.target.value.toUpperCase())}
                required
                disabled={isLoading}
                autoFocus
              />
              <p className="text-xs text-cyan-700 mt-3 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Contact your school administrator for this code
              </p>
            </div>

            <button
              type="submit"
              className={`w-full bg-gradient-to-r from-cyan-600 to-blue-700 text-white font-semibold py-4 px-6 rounded-2xl
                         hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 text-base tracking-wide mb-4 ${
                isLoading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
              disabled={isLoading}
            >
              {isLoading ? 'Validating Code...' : 'Continue →'}
            </button>
          </>
        )}

        {/* Step 2: Complete Registration with Pre-filled Data */}
        {step === 2 && teacherData && (
          <>
            {/* School Information Banner */}
            <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-2xl">
              <p className="text-sm text-green-900 font-bold flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Code Verified
              </p>
              <p className="text-xs text-green-700 mt-2 ml-7">
                {schoolData?.school_name}
                {schoolData?.district_name && ` • ${schoolData.district_name}`}
              </p>
            </div>

            {/* Pre-filled Fields (Read-only) */}
            <div className="mb-6 p-4 bg-gray-50 rounded-2xl border-2 border-gray-200">
              <p className="text-xs text-gray-600 mb-3 font-bold">Pre-filled Information (from your administrator)</p>

              <div className="form-group mb-3">
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Full Name
                </label>
                <input
                  type="text"
                  value={teacherData.full_name}
                  className="w-full py-2 px-3 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-600 cursor-not-allowed"
                  disabled
                  readOnly
                />
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="form-group">
                  <label className="block mb-2 text-sm font-semibold text-gray-700">
                    Subject/Grade
                  </label>
                  <input
                    type="text"
                    value={teacherData.subject || 'Not specified'}
                    className="w-full py-2 px-3 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-600 cursor-not-allowed"
                    disabled
                    readOnly
                  />
                </div>
                <div className="form-group">
                  <label className="block mb-2 text-sm font-semibold text-gray-700">
                    Grade Level
                  </label>
                  <input
                    type="text"
                    value={teacherData.grade_level ? teacherData.grade_level.charAt(0).toUpperCase() + teacherData.grade_level.slice(1) : 'Not specified'}
                    className="w-full py-2 px-3 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-600 cursor-not-allowed capitalize"
                    disabled
                    readOnly
                  />
                </div>
              </div>

              {teacherData.classroom_number && (
                <div className="form-group">
                  <label className="block mb-2 text-sm font-semibold text-gray-700">
                    Classroom
                  </label>
                  <input
                    type="text"
                    value={teacherData.classroom_number}
                    className="w-full py-2 px-3 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-600 cursor-not-allowed"
                    disabled
                    readOnly
                  />
                </div>
              )}
            </div>

            {/* Editable Fields */}
            <p className="text-xs text-gray-600 mb-4 font-bold">Complete Your Registration</p>

            <div className="space-y-4 mb-6">
              <div className="form-group">
                <label htmlFor="email" className="block mb-2 font-semibold text-gray-800 text-sm">
                  School Email Address*
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="teacher@school.edu"
                  className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-cyan-500 transition-colors"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  autoComplete="email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="block mb-2 font-semibold text-gray-800 text-sm">
                  Password*
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  placeholder="Create a password (min 8 characters)"
                  className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-cyan-500 transition-colors"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                  minLength={8}
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirm-password" className="block mb-2 font-semibold text-gray-800 text-sm">
                  Confirm Password*
                </label>
                <input
                  type="password"
                  id="confirm-password"
                  name="confirmPassword"
                  placeholder="Re-enter password"
                  className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-cyan-500 transition-colors"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <button
              type="submit"
              className={`w-full bg-gradient-to-r from-cyan-600 to-blue-700 text-white font-semibold py-4 px-6 rounded-2xl
                         hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 text-base tracking-wide mb-4 ${
                isLoading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Create Teacher Account'}
            </button>
          </>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border-2 border-red-200 rounded-xl text-red-700 text-sm text-center">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border-2 border-green-200 rounded-xl text-green-700 text-sm text-center font-semibold">
            {success}
          </div>
        )}

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-400">Already have an account?</span>
          </div>
        </div>

        <Link
          to="/login/teacher"
          className="block text-center w-full bg-gray-100 text-gray-800 font-semibold py-4 px-6 rounded-2xl
                   hover:bg-gray-200 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 text-base"
        >
          Sign In
        </Link>
      </form>
    </AuthLayout>
  );
};

export default TeacherRegisterPage;
